import React, { useState } from 'react';
import { motion } from 'framer-motion';

const Portfolio = () => {
  const [activeFilter, setActiveFilter] = useState('all');

  // Updated project images - all unique, high-quality renovation photos
  const projects = [
    {
      id: 1,
      title: 'Modern Kitchen Renovation',
      category: 'kitchen',
      image: 'https://images.unsplash.com/photo-1556911220-bff31c812dba?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
      description: 'Complete kitchen transformation with custom cabinets, granite countertops, and stainless steel appliances.'
    },
    {
      id: 2,
      title: 'Luxury Kitchen Remodel',
      category: 'kitchen',
      image: 'https://images.unsplash.com/photo-1556909114-f6e7ad7d3136?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
      description: 'High-end kitchen renovation with marble countertops, custom island, and premium appliances.'
    },
    {
      id: 3,
      title: 'Master Bathroom Remodel',
      category: 'bathroom',
      image: 'https://images.unsplash.com/photo-1552321554-5fefe8c9ef14?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
      description: 'Luxury master bathroom with custom tile work, double vanity, and walk-in shower.'
    },
    {
      id: 4,
      title: 'Spa-Style Bathroom',
      category: 'bathroom',
      image: 'https://images.unsplash.com/photo-1620626011761-996317b8d101?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
      description: 'Modern bathroom renovation with freestanding tub, glass shower, and contemporary fixtures.'
    },
    {
      id: 5,
      title: 'Home Addition Project',
      category: 'residential',
      image: 'https://images.unsplash.com/photo-1564013799919-ab600027ffc6?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
      description: 'Two-story home addition with matching exterior materials and seamless integration.'
    },
    {
      id: 6,
      title: 'Basement Finishing',
      category: 'residential',
      image: 'https://images.unsplash.com/photo-1586023492125-27b2c045efd7?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
      description: 'Complete basement renovation creating additional living space with family room and bar area.'
    },
    {
      id: 7,
      title: 'Whole Home Renovation',
      category: 'residential',
      image: 'https://images.unsplash.com/photo-1560448204-e02f11c3d0e2?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
      description: 'Complete home transformation with open floor plan, modern finishes, and updated systems.'
    },
    {
      id: 8,
      title: 'Office Renovation',
      category: 'commercial',
      image: 'https://images.unsplash.com/photo-1497366216548-37526070297c?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
      description: 'Professional office renovation with modern lighting, flooring, and workspace design.'
    },
    {
      id: 9,
      title: 'Retail Store Buildout',
      category: 'commercial',
      image: 'https://images.unsplash.com/photo-1441986300917-64674bd600d8?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
      description: 'Custom retail space buildout with modern fixtures, lighting, and display areas.'
    }
  ];

  const filters = [
    { key: 'all', label: 'All Projects' },
    { key: 'kitchen', label: 'Kitchens' },
    { key: 'bathroom', label: 'Bathrooms' },
    { key: 'residential', label: 'Residential' },
    { key: 'commercial', label: 'Commercial' }
  ];

  const filteredProjects = activeFilter === 'all' 
    ? projects 
    : projects.filter(project => project.category === activeFilter);

  return (
    <section id="portfolio" className="py-20 bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            Our Portfolio
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto mb-8">
            Take a look at some of our recent residential and commercial contracting projects showcasing the quality craftsmanship that sets us apart.
          </p>
          {/* Filter Buttons */}
          <div className="flex flex-wrap justify-center gap-4">
            {filters.map((filter) => (
              <button
                key={filter.key}
                onClick={() => setActiveFilter(filter.key)}
                className={`px-6 py-3 rounded-lg font-medium transition-all ${
                  activeFilter === filter.key
                    ? 'bg-blue-600 text-white shadow-lg'
                    : 'bg-white text-gray-700 hover:bg-gray-100'
                }`}
              >
                {filter.label}
              </button>
            ))}
          </div>
        </motion.div>

        {/* Projects Grid */}
        <motion.div
          layout
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8"
        >
          {filteredProjects.map((project, index) => (
            <motion.div
              key={project.id}
              layout
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
              transition={{ delay: index * 0.1 }}
              className="bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-all duration-300 group"
            >
              <div className="relative overflow-hidden">
                <img
                  src={project.image}
                  alt={project.title}
                  className="w-full h-64 object-cover group-hover:scale-110 transition-transform duration-500"
                  loading="lazy"
                />
                <div className="absolute top-4 left-4">
                  <span
                    className={`px-3 py-1 rounded-full text-xs font-medium ${
                      project.category === 'commercial'
                        ? 'bg-green-100 text-green-800'
                        : project.category === 'kitchen'
                        ? 'bg-blue-100 text-blue-800'
                        : project.category === 'bathroom'
                        ? 'bg-purple-100 text-purple-800'
                        : 'bg-orange-100 text-orange-800'
                    }`}
                  >
                    {project.category.charAt(0).toUpperCase() + project.category.slice(1)}
                  </span>
                </div>
              </div>
              <div className="p-6">
                <h3 className="text-xl font-bold text-gray-900 mb-2">
                  {project.title}
                </h3>
                <p className="text-gray-600">{project.description}</p>
              </div>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

export default Portfolio;